import * as angular from 'angular';
import { NavigationService } from 'services/navigation';
import * as moment from 'moment';
import { LoggingService } from 'services/logging';

export const module = angular.module('authenticationService', ['ngCookies']);

interface AuthenticationStorage {
    [key: string]: string | undefined;
}

export class AuthenticationService {
    private $cookies: angular.cookies.ICookiesService;
    private localStorage: Storage;
    private sessionStorage: Storage;
    private $window: angular.IWindowService;
    private $document: angular.IDocumentService;
    private navigationService: NavigationService;
    private authenticationStorage: AuthenticationStorage;
    private loggingService: LoggingService;
    private $location: angular.ILocationService;
    private isAuthenticating: boolean;

    constructor(
        $cookies: angular.cookies.ICookiesService,
        $window: angular.IWindowService,
        $document: angular.IDocumentService,
        navigationService: NavigationService,
        loggingService: LoggingService,
        $location: angular.ILocationService
    ) {
        this.$cookies = $cookies;
        this.localStorage = $window.localStorage;
        this.sessionStorage = $window.sessionStorage;
        this.$window = $window;
        this.$document = $document;
        this.navigationService = navigationService;
        this.authenticationStorage = {
            id_token: undefined
        };
        this.loggingService = loggingService;
        this.$location = $location;
        this.isAuthenticating = false;
    }

    getCookieDomain() {
        const host = this.$location.host();
        const hostHasSubdomain = host.replace(/[^.]/g, '').length > 1;

        if (host === 'localhost') {
            return host;
        } else if (!hostHasSubdomain) {
            // localhost also doesn't have a subdomain, but I'm keeping it separate for clarity
            return host;
        } else {
            return host.substr(host.indexOf('.')); /* strip off the subdomain */
        }
    }

    setCookie(key: string, data: string, options?: angular.cookies.ICookiesOptions) {
        this.$cookies.put(key, data, {
            ...options,
            path: '/',
            domain: this.getCookieDomain()
        });
    }

    removeCookie(key: string, options?: angular.cookies.ICookiesOptions) {
        // temporarily also remove subdomain cookies which may have been set previously
        this.$cookies.remove(key, {
            ...options
        });

        this.$cookies.remove(key, {
            ...options,
            path: '/',
            domain: this.getCookieDomain()
        });
    }

    setAuthenticationStorage(key: string, data: string) {
        this.authenticationStorage[key] = data;
    }

    unsetAuthenticationStorage(key: string) {
        this.authenticationStorage[key] = undefined;
    }

    setPersistentCookie(key: string, data: string) {
        this.loggingService.log('setting persistent cookie');

        try {
            // might fail in Safari in private browsing mode, or other cases
            this.setCookie(key, data, {
                expires: moment()
                    .add(30, 'days')
                    .toDate()
            });
        } catch (e) {
            this.loggingService.log('error setting cookie', e);
            this.setAuthenticationStorage(key, data);
        }
    }

    setSessionCookie(key: string, data: string) {
        this.loggingService.log('setting session cookie');
        try {
            // might fail in Safari in private browsing mode, or other cases
            this.setCookie(key, data);
        } catch (e) {
            this.setAuthenticationStorage(key, data);
        }
    }

    setAuthenticationToken(idToken: string) {
        this.loggingService.log('setting auth token');

        const remember = !!this.$cookies.get('remember');

        if (remember) {
            this.setPersistentCookie('id_token', idToken);
        } else {
            this.setSessionCookie('id_token', idToken);
        }
    }

    getAuthenticationToken() {
        if (this.$cookies.get('id_token')) {
            return this.$cookies.get('id_token');
        } else {
            return this.authenticationStorage.id_token;
        }
    }

    forgetAuthenticationToken() {
        this.loggingService.log('forgetting auth token');

        try {
            this.removeCookie('id_token');
            this.removeCookie('remember');
        } catch (e) {
            this.loggingService.log('error removing cookie', e);
            // ignore
        }

        this.unsetAuthenticationStorage('id_token');
    }

    setIsAuthenticating(isAuthenticating: boolean) {
        this.isAuthenticating = isAuthenticating;
    }

    getIsAuthenticating() {
        return this.isAuthenticating;
    }

    logout() {
        this.forgetAuthenticationToken();
        this.navigationService.toLoginPage();
    }

    getAuth0Config() {
        return (this.$window.appConfig && this.$window.appConfig.AUTH0) || {};
    }
}

module.factory('authenticationService', function(
    $cookies: angular.cookies.ICookiesService,
    $window: angular.IWindowService,
    $document: angular.IDocumentService,
    navigationService: NavigationService,
    loggingService: LoggingService,
    $location: angular.ILocationService
) {
    'ngInject';

    return new AuthenticationService($cookies, $window, $document, navigationService, loggingService, $location);
});
